import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const AssignmentDetails = ({ assignment, onClose, onEdit, onCancel }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'in-progress':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'completed':
        return 'bg-success/10 text-success border-success/20';
      case 'cancelled':
        return 'bg-error/10 text-error border-error/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'text-error';
      case 'high':
        return 'text-warning';
      case 'medium':
        return 'text-primary';
      case 'low':
        return 'text-muted-foreground';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-2">
              Assignment Details
            </h2>
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(assignment?.status)}`}>
              <div className="w-2 h-2 rounded-full bg-current" />
              {assignment?.status?.charAt(0)?.toUpperCase() + assignment?.status?.slice(1)}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-caption text-muted-foreground">Ticket ID</label>
            <p className="text-base font-medium text-foreground">{assignment?.ticketId}</p>
          </div>
          <div>
            <label className="text-sm font-caption text-muted-foreground">Priority</label>
            <p className={`text-base font-medium ${getPriorityColor(assignment?.priority)}`}>
              {assignment?.priority?.charAt(0)?.toUpperCase() + assignment?.priority?.slice(1)}
            </p>
          </div>
        </div>
      </div>
      <div className="p-4 md:p-6 space-y-6">
        <div>
          <h3 className="text-base font-heading font-semibold text-foreground mb-3">
            Schedule Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Calendar" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <p className="text-sm font-caption text-muted-foreground">Date</p>
                <p className="text-sm font-medium text-foreground">
                  {new Date(assignment.scheduledDate)?.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Clock" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <p className="text-sm font-caption text-muted-foreground">Time</p>
                <p className="text-sm font-medium text-foreground">{assignment?.scheduledTime}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Timer" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <p className="text-sm font-caption text-muted-foreground">Duration</p>
                <p className="text-sm font-medium text-foreground">{assignment?.estimatedDuration} hours</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="MapPin" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <p className="text-sm font-caption text-muted-foreground">Location</p>
                <p className="text-sm font-medium text-foreground">{assignment?.location}</p>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-base font-heading font-semibold text-foreground mb-3">
            Assigned Technicians
          </h3>
          <div className="space-y-3">
            {assignment?.technicians?.map((tech) => (
              <div key={tech?.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-muted flex-shrink-0">
                  <Image 
                    src={tech?.avatar} 
                    alt={tech?.avatarAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground">{tech?.name}</p>
                    {tech?.isPrimary && (
                      <span className="px-2 py-0.5 bg-primary text-primary-foreground text-xs font-medium rounded">
                        Primary
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{tech?.phone}</p>
                </div>
                <button className="p-2 rounded-lg hover:bg-muted transition-smooth">
                  <Icon name="Phone" size={18} color="var(--color-primary)" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {assignment?.notes && (
          <div>
            <h3 className="text-base font-heading font-semibold text-foreground mb-2">
              Assignment Notes
            </h3>
            <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
              {assignment?.notes}
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          {assignment?.status === 'scheduled' && (
            <>
              <Button
                variant="outline"
                fullWidth
                iconName="Edit"
                iconPosition="left"
                onClick={onEdit}
              >
                Edit Assignment
              </Button>
              <Button
                variant="destructive"
                fullWidth
                iconName="X"
                iconPosition="left"
                onClick={onCancel}
              >
                Cancel Assignment
              </Button>
            </>
          )}
          {assignment?.status === 'in-progress' && (
            <Button
              variant="success"
              fullWidth
              iconName="Check"
              iconPosition="left"
            >
              Mark as Completed
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignmentDetails;